import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import type { Achievement, BuildStat, PerformanceRecord } from '../../types/buildExpansion';
import { BILT_MARK_MONO_SRC } from '../../utils/biltBrandDisplay';
import {
  loadImageSafe,
  renderShareCard,
  type ShareCardFormat,
  type ShareCardHighlight,
} from '../../utils/shareCardCanvas';

export interface ShareCardVehicleInfo {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  nickname?: string | null;
}

export interface ShareCardAward {
  id: number;
  title: string;
  placement?: string | null;
  event_title?: string | null;
}

export interface ShareCardGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: ShareCardVehicleInfo;
  /** Full public share URL for the vehicle. */
  shareUrl: string;
  vehicleId?: number;
  ownerName?: string | null;
  /** Public vehicle hero image URL; card degrades to typographic if it fails. */
  imageUrl?: string | null;
  /** Existing QR image URL for this vehicle (optional footer QR). */
  qrImageUrl?: string | null;
  stats?: BuildStat[];
  achievements?: Achievement[];
  /** Dyno and personal-best records used for highlight options. */
  records?: PerformanceRecord[];
  awards?: ShareCardAward[];
}

interface FormatChoice {
  value: ShareCardFormat;
  label: string;
  caption: string;
  previewClass: string;
}

const FORMAT_CHOICES: FormatChoice[] = [
  { value: 'story', label: 'Story', caption: '9:16', previewClass: 'w-5 h-9' },
  { value: 'portrait', label: 'Post', caption: '4:5', previewClass: 'w-7 h-9' },
  { value: 'square', label: 'Square', caption: '1:1', previewClass: 'w-8 h-8' },
];

interface HighlightOption {
  key: string;
  label: string;
  caption: string | null;
  highlight: ShareCardHighlight;
  verified: boolean;
}

const MAX_OPTIONS_PER_KIND = 3;

function buildHighlightOptions(
  achievements: Achievement[],
  awards: ShareCardAward[],
  records: PerformanceRecord[]
): HighlightOption[] {
  const options: HighlightOption[] = [];

  achievements
    .filter((a) => Boolean(a.name) && (a.status === 'current' || a.status === null))
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((a) => {
      const detail =
        a.rank != null
          ? `Ranked #${a.rank}${a.population != null ? ` of ${a.population}` : ''}`
          : a.short_name && a.short_name !== a.name
            ? a.short_name
            : null;
      options.push({
        key: `achievement-${a.id}`,
        label: a.name as string,
        caption: detail,
        highlight: { kind: 'achievement', title: a.name as string, detail },
        verified: a.achievement_class === 'verified_credential',
      });
    });

  awards.slice(0, MAX_OPTIONS_PER_KIND).forEach((award) => {
    const detail = [award.placement, award.event_title].filter(Boolean).join(' · ') || null;
    options.push({
      key: `award-${award.id}`,
      label: award.title,
      caption: detail,
      highlight: { kind: 'award', title: award.title, detail },
      verified: true,
    });
  });

  records
    .filter((r) => r.record_type === 'dyno')
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((r) => {
      const title = r.display_value || r.title || r.record_type_label;
      const detail =
        [r.dyno_type, r.shop_name || r.venue_name, r.date_recorded].filter(Boolean).join(' · ') ||
        null;
      options.push({
        key: `dyno-${r.id}`,
        label: title,
        caption: detail,
        highlight: { kind: 'dyno', title, detail },
        verified: r.is_verified,
      });
    });

  records
    .filter((r) => r.is_personal_best && r.record_type !== 'dyno')
    .slice(0, MAX_OPTIONS_PER_KIND)
    .forEach((r) => {
      const title = r.display_value || r.title || r.record_type_label;
      const detail =
        [r.record_type_label, r.venue_name, r.date_recorded].filter(Boolean).join(' · ') || null;
      options.push({
        key: `pb-${r.id}`,
        label: title,
        caption: detail,
        highlight: { kind: 'personal_best', title, detail },
        verified: r.is_verified,
      });
    });

  return options;
}

const ShareCardGenerator: React.FC<ShareCardGeneratorProps> = ({
  isOpen,
  onClose,
  vehicle,
  shareUrl,
  vehicleId,
  ownerName,
  imageUrl,
  qrImageUrl,
  stats = [],
  achievements = [],
  records = [],
  awards = [],
}) => {
  const { showToast } = useToast();

  const [format, setFormat] = useState<ShareCardFormat>('story');
  const [highlightKey, setHighlightKey] = useState<string>('none');
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const vehicleImgRef = useRef<HTMLImageElement | null>(null);
  const qrImgRef = useRef<HTMLImageElement | null>(null);
  const brandMarkRef = useRef<HTMLImageElement | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const baseTitle = [vehicle.year, vehicle.make, vehicle.model]
    .filter((part) => part !== null && part !== undefined && `${part}`.trim() !== '')
    .join(' ');
  const cardTitle = baseTitle || vehicle.nickname || 'Bilt build';
  const cardSubtitle = baseTitle && vehicle.nickname ? `"${vehicle.nickname}"` : null;
  const shortUrl = shareUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const highlightOptions = useMemo(
    () => buildHighlightOptions(achievements, awards, records),
    [achievements, awards, records]
  );

  const canNativeShare = useMemo(() => {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.share !== 'function' ||
      typeof navigator.canShare !== 'function'
    ) {
      return false;
    }
    try {
      return navigator.canShare({
        files: [new File([new Blob()], 'share.png', { type: 'image/png' })],
      });
    } catch {
      return false;
    }
  }, []);

  // Load the vehicle image, QR, and Bilt badge mark once per open.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setAssetsLoaded(false);
    Promise.all([
      imageUrl ? loadImageSafe(imageUrl) : Promise.resolve(null),
      qrImageUrl ? loadImageSafe(qrImageUrl) : Promise.resolve(null),
      loadImageSafe(BILT_MARK_MONO_SRC),
    ]).then(([vehicleImg, qrImg, brandMark]) => {
      if (cancelled) return;
      vehicleImgRef.current = vehicleImg;
      qrImgRef.current = qrImg;
      brandMarkRef.current = brandMark;
      setAssetsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, imageUrl, qrImageUrl]);

  const selectedOption = useMemo(
    () => highlightOptions.find((o) => o.key === highlightKey) ?? null,
    [highlightOptions, highlightKey]
  );

  // Re-render the preview whenever selections or loaded assets change.
  useEffect(() => {
    if (!isOpen || !assetsLoaded) return undefined;
    let cancelled = false;
    setRendering(true);
    setRenderFailed(false);

    const topStats = stats.slice(0, 4);
    const verifiedLabel = selectedOption?.verified
      ? 'Verified result'
      : topStats.some((s) => s.verified)
        ? 'Verified specs'
        : null;

    renderShareCard({
      format,
      title: cardTitle,
      subtitle: cardSubtitle,
      ownerName: ownerName || null,
      stats: topStats.map((s) => ({ label: s.label, value: s.value })),
      highlight: selectedOption?.highlight ?? null,
      image: vehicleImgRef.current,
      shortUrl,
      qrImage: qrImgRef.current,
      brandMark: brandMarkRef.current,
      dateText: new Date().toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      verifiedLabel,
    })
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setRenderFailed(true);
        showToast('Could not render the share card. Please try again.', 'error', {
          dedupeKey: 'share-card-render-failed',
        });
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stats/vehicle props are stable per open
  }, [isOpen, assetsLoaded, format, selectedOption]);

  // Clean up the preview object URL when the modal closes or unmounts.
  useEffect(() => {
    if (isOpen) return undefined;
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      blobRef.current = null;
      setPreviewUrl(null);
    };
  }, [isOpen]);

  const fileName = useMemo(() => {
    const slug = cardTitle.replace(/\s+/g, '-').toLowerCase().slice(0, 40);
    return `bilt-share-${format}-${slug}.png`;
  }, [cardTitle, format]);

  const trackGenerated = useCallback(
    (action: 'download' | 'native_share') => {
      void apiService.trackEvent('share_card_generated', {
        vehicle_id: vehicleId,
        metadata: {
          format,
          highlight: selectedOption?.highlight.kind ?? 'none',
          action,
        },
      });
    },
    [vehicleId, format, selectedOption]
  );

  const handleDownload = useCallback(() => {
    const blob = blobRef.current;
    if (!blob) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    trackGenerated('download');
    showToast('Share card downloaded.', 'success');
  }, [fileName, trackGenerated, showToast]);

  const handleNativeShare = useCallback(async () => {
    const blob = blobRef.current;
    if (!blob) return;
    try {
      const file = new File([blob], fileName, { type: 'image/png' });
      await navigator.share({ files: [file], title: cardTitle, url: shareUrl });
      trackGenerated('native_share');
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      showToast('Sharing failed. You can download the card instead.', 'error');
    }
  }, [fileName, cardTitle, shareUrl, trackGenerated, showToast]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      showToast('Link copied.', 'success', { dedupeKey: 'share-card-link-copied' });
    } catch {
      showToast('Could not copy the link.', 'error');
    }
  }, [shareUrl, showToast]);

  const formatLabel = FORMAT_CHOICES.find((f) => f.value === format)?.label ?? format;
  const busy = rendering || !assetsLoaded;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create share card" size="xl">
      <div className="grid gap-6 md:grid-cols-[300px,minmax(0,1fr)]">
        <div>
          <fieldset className="mb-6">
            <legend className="text-sm font-medium text-gray-300 mb-2">Format</legend>
            <div className="grid grid-cols-3 gap-2">
              {FORMAT_CHOICES.map((choice) => (
                <label key={choice.value} className="cursor-pointer">
                  <input
                    type="radio"
                    name="share-card-format"
                    value={choice.value}
                    checked={format === choice.value}
                    onChange={() => setFormat(choice.value)}
                    className="sr-only peer"
                  />
                  <span className="flex flex-col items-center gap-1.5 rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-2 py-3 transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                    <span
                      aria-hidden
                      className={`rounded-sm bg-[#1a1a1a] ${choice.previewClass}`}
                    />
                    <span className="text-sm font-medium text-white">{choice.label}</span>
                    <span className="text-xs text-gray-500">{choice.caption}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {highlightOptions.length > 0 && (
            <fieldset className="mb-6">
              <legend className="text-sm font-medium text-gray-300 mb-2">Highlight</legend>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                <label className="block cursor-pointer">
                  <input
                    type="radio"
                    name="share-card-highlight"
                    value="none"
                    checked={highlightKey === 'none'}
                    onChange={() => setHighlightKey('none')}
                    className="sr-only peer"
                  />
                  <span className="block rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-3 py-2 text-sm text-white transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                    None
                  </span>
                </label>
                {highlightOptions.map((option) => (
                  <label key={option.key} className="block cursor-pointer">
                    <input
                      type="radio"
                      name="share-card-highlight"
                      value={option.key}
                      checked={highlightKey === option.key}
                      onChange={() => setHighlightKey(option.key)}
                      className="sr-only peer"
                    />
                    <span className="block rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-3 py-2 transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                      <span className="block text-sm text-white">{option.label}</span>
                      {option.caption && (
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {option.caption}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <p className="text-xs text-gray-500">
            Cards only include what is shown on your public build page.
          </p>
        </div>

        <div className="flex flex-col">
          <div
            className="flex items-center justify-center rounded-xl border border-white/[0.06] bg-[#0a0a0a]/60 p-4 min-h-[300px]"
            aria-live="polite"
          >
            {busy ? (
              <div role="status" className="flex flex-col items-center gap-3 text-gray-400">
                <span className="spinner-sm" aria-hidden />
                <span className="text-sm">Rendering preview…</span>
              </div>
            ) : renderFailed ? (
              <p className="text-sm text-red-300 text-center px-4">
                Preview failed to render. Adjust the options or try again.
              </p>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt={`Share card preview for ${cardTitle} in ${formatLabel} format`}
                className="max-h-[340px] w-auto rounded-lg shadow-lg shadow-black/40"
              />
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="primary"
              onClick={handleDownload}
              disabled={busy || renderFailed || !previewUrl}
              loading={busy}
              loadingLabel="Rendering…"
            >
              Download PNG
            </Button>
            {canNativeShare && (
              <Button
                variant="secondary"
                onClick={handleNativeShare}
                disabled={busy || renderFailed || !previewUrl}
              >
                Share
              </Button>
            )}
            <Button variant="secondary" onClick={handleCopyLink}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareCardGenerator;
